import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listOrganizationsForCurator,
  createOrganization,
  ApiError,
  type OrgListRow,
  type OrganizationProfile,
} from "../api";
import { OrgDrawer } from "./OrgDrawer";
import { TrailDrawer } from "./TrailDrawer";

/**
 * Organization management card — top-of-page launchpad for the
 * curator's CRM surface. Lists every user-created org with its
 * trail count, opens OrgDrawer for inline editing, and provides a
 * "+ New organization" inline-create path.
 *
 * Apple HIG + Laws of UX:
 *   - Compact rows (avatar + name + count + chevron) so 5+ orgs fit
 *     above the fold without scrolling (Doherty / Hick's)
 *   - Primary "Create" CTA at the top-right of the card so the user
 *     never has to scroll to the bottom to add one (Fitts's Law —
 *     the action is always within reach of the heading they just
 *     read)
 *   - Single-tap to edit (open drawer) — no extra "manage" button
 *   - Tile-row pattern matches every other "list of entities" card
 *     in the panel (catalog rows, claims, etc.) so visual rhythm
 *     stays consistent (Jakob's Law)
 */
/**
 * Curator-side org management. Per-org analytics is NOT exposed
 * here — that drill-down belongs to the owner / steward of the org
 * via the My Trails admin workspace. Curators are TSP platform
 * staff; their analytics surface is the platform-wide rollup card.
 */
export function OrgManagementCard() {
  const [orgs, setOrgs] = useState<OrgListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  /** Cross-drawer nav target — when the user taps a trail row inside
   *  an OrgDrawer, we close that drawer and open TrailDrawer for the
   *  slug. Keeps the modal stack at 1 (Apple HIG: no nested modals). */
  const [editingTrailSlug, setEditingTrailSlug] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listOrganizationsForCurator()
      .then((r) => {
        if (!cancelled) setOrgs(r.organizations);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : "Couldn't load organizations."
        );
        setOrgs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const totalOrgs = orgs?.length ?? 0;
  const totalTrails = useMemo(
    () => (orgs ?? []).reduce((sum, o) => sum + o.trailCount, 0),
    [orgs]
  );

  const handleSavedOrg = useCallback((updated: OrganizationProfile) => {
    // Patch in-place rather than refetching the whole list — keeps
    // scroll position stable + avoids a network round-trip.
    setOrgs((prev) =>
      prev
        ? prev.map((o) =>
            o.id === updated.id
              ? {
                  ...o,
                  name: updated.name,
                  slug: updated.slug,
                  logoUrl: updated.logoUrl,
                  websiteUrl: updated.websiteUrl,
                  donationUrl: updated.donationUrl,
                }
              : o
          )
        : prev
    );
  }, []);

  return (
    <div id="curator-orgs" className="card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h3 style={{ margin: 0 }}>Organizations</h3>
          <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
            {totalOrgs === 0
              ? "Stand up a real-world org (e.g. SORBA Woodstock) and link trails to it."
              : `${totalOrgs} org${totalOrgs === 1 ? "" : "s"} · ${totalTrails} trail${totalTrails === 1 ? "" : "s"} linked`}
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            className="primary small"
            onClick={() => {
              setCreating(true);
              setNewName("");
              setCreateError(null);
            }}
          >
            + New organization
          </button>
        )}
      </div>

      {creating && (
        <div className="org-create-row" style={{ marginTop: 12 }}>
          <input
            type="text"
            autoFocus
            placeholder="SORBA Woodstock"
            value={newName}
            disabled={createBusy}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim().length > 0) {
                void handleCreate();
              } else if (e.key === "Escape") {
                setCreating(false);
                setNewName("");
                setCreateError(null);
              }
            }}
          />
          <button
            type="button"
            className="primary small"
            disabled={createBusy || newName.trim().length === 0}
            onClick={handleCreate}
          >
            {createBusy ? "Creating…" : "Create"}
          </button>
          <button
            type="button"
            className="ghost small"
            disabled={createBusy}
            onClick={() => {
              setCreating(false);
              setNewName("");
              setCreateError(null);
            }}
          >
            Cancel
          </button>
        </div>
      )}
      {createError && (
        <div className="error" style={{ marginTop: 8 }}>
          {createError}
        </div>
      )}

      {error && (
        <div className="error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
      {orgs === null && (
        <div className="muted" style={{ marginTop: 12 }}>
          Loading organizations…
        </div>
      )}
      {orgs !== null && orgs.length === 0 && !error && (
        <div className="empty" style={{ marginTop: 12 }}>
          No organizations yet. Use "+ New organization" above to add one.
        </div>
      )}
      {orgs !== null && orgs.length > 0 && (
        <ul className="org-mgmt-list">
          {orgs.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className="org-mgmt-row"
                onClick={() => setEditingOrgId(o.id)}
              >
                {o.logoUrl ? (
                  <img
                    src={o.logoUrl}
                    alt=""
                    className="org-mgmt-logo"
                  />
                ) : (
                  <span
                    className="org-mgmt-logo placeholder"
                    aria-hidden="true"
                  >
                    🏔
                  </span>
                )}
                <div className="org-mgmt-row-main">
                  <div className="org-mgmt-row-name">{o.name}</div>
                  <div className="org-mgmt-row-sub muted">
                    {o.trailCount === 0
                      ? "No trails linked yet"
                      : `${o.trailCount} trail${o.trailCount === 1 ? "" : "s"} linked`}
                  </div>
                </div>
                <span className="org-mgmt-row-chev" aria-hidden="true">›</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editingOrgId && (
        <OrgDrawer
          orgId={editingOrgId}
          onClose={() => setEditingOrgId(null)}
          onSaved={handleSavedOrg}
          onEditTrail={(slug) => {
            // Hand off — close the org drawer and open the trail
            // drawer in its place. Doesn't restore the org drawer
            // afterwards by design: editing a trail is its own task
            // (and the launchpad is right there if the curator wants
            // back to the org).
            setEditingOrgId(null);
            setEditingTrailSlug(slug);
          }}
        />
      )}
      {editingTrailSlug && (
        <TrailDrawer
          slug={editingTrailSlug}
          onClose={() => setEditingTrailSlug(null)}
          onSaved={() => {
            // Trail edits can change the trail's org link — reload
            // the card so trailCount stays canonical.
            setReloadKey((k) => k + 1);
          }}
          onDeleted={(deletedSlug) => {
            if (deletedSlug === editingTrailSlug) {
              setEditingTrailSlug(null);
            }
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      await createOrganization(name);
      // Refetch via reloadKey so we get the canonical row shape +
      // trailCount = 0 without bespoke patching.
      setReloadKey((k) => k + 1);
      setCreating(false);
      setNewName("");
    } catch (err) {
      setCreateError(
        err instanceof ApiError ? err.message : "Couldn't create the organization."
      );
    } finally {
      setCreateBusy(false);
    }
  }
}
